import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { Rating } from '@material-ui/lab';

export interface QualitativeMetricsProps {
    specQualitativeResultsUrl: string;
    specFile?: string;
    lintingStatus?: string;
}

const QualitativeMetrics: React.FC<QualitativeMetricsProps> = ({ specQualitativeResultsUrl, specFile, lintingStatus }) => {
    const [qualitativeMetrics, setQualitativeMetrics] = useState<any>(null);
    const identityEnvironment = useSelector((state: any) => state?.common?.identityEnvironment ?? "trimble-pre-prod");
    const [qualitativeLoading, setQualitativeLoading] = useState(true);

    useEffect(() => {

        const fetchQualitativeMetrics = async () => {
            try {
                if(specQualitativeResultsUrl !== "") {
                    const response = await fetch(`${specQualitativeResultsUrl}`);
                    if (!response.ok) throw new Error("Failed to fetch qualitative metrics");
                    const jsonData = await response.json();
                    setQualitativeMetrics(jsonData);
                }
            } catch (err) {
                setQualitativeMetrics(null); 
            } finally {
                setQualitativeLoading(false);
            }
        };

        fetchQualitativeMetrics();
    }, [identityEnvironment, specQualitativeResultsUrl]);


    const calculateOverallScore = (metrics: any) => {
        const scores = Object.entries(metrics)
            .filter(([key, value]) => typeof value === 'number') 
            .map(([_, value]) => value as number);
        const total = scores.reduce((acc, score) => acc + score, 0);
        return scores.length > 0 ? total / scores.length : 0; 
    };


    return (
        <Box>
            <Box>
                {qualitativeLoading ? (
                    <CircularProgress />
                ) : qualitativeMetrics ? (
                    <Box>
                        <Box mb={3} textAlign="center">
                            <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                                Quality of API Specification
                            </Typography>
                            <Rating
                                name="overall-score"
                                value={calculateOverallScore(qualitativeMetrics) / 2} 
                                precision={0.1}
                                readOnly
                                size="large"
                                style={{ color: '#FFA500' }} 
                            />
                        </Box>

                        {/* Metrics Table */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px' }}>
                            <thead>
                                <tr>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Metric</th>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(qualitativeMetrics)
                                    .filter(([key]) => typeof qualitativeMetrics[key] === 'number')
                                    .map(([key, value], index) => (
                                        <tr key={index}>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                                {key
                                                    .replace(/([A-Z])/g, ' $1') 
                                                    .replace(/^./, (str) => str.toUpperCase())}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                                {value as string | number}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </Box>
                ) : (
                    <Typography variant="body1">{specFile ? lintingStatus !== "IN_PROGRESS" ? "Qualitative metrics are unable to process, please re-upload again" : "Qualitative metrics are processing, please check back later." : "No qualitative metrics available."}</Typography>
                )}
            </Box>
        </Box>
    );
};

export default QualitativeMetrics;
